import { useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MarketplaceProfileService } from '../../core/services';
import {
  MarketplaceModule,
  MarketplaceRequestPayload,
  MarketplaceRequestResponse,
} from './interface';

type RequestMarketplaceProps = {
  onSuccess: (data: MarketplaceRequestResponse) => void;
  onError: (err: unknown) => void;
};

type MarketplaceModulesPayload = MarketplaceModule[] | { data?: MarketplaceModule[] };

export const normalizeMarketplaceModules = (res: unknown): MarketplaceModule[] => {
  if (Array.isArray(res)) return res;

  if (res && typeof res === 'object' && Array.isArray((res as MarketplaceModulesPayload).data)) {
    return (res as { data: MarketplaceModule[] }).data;
  }

  return [];
};

export const normalizeMarketplacePagedContent = (res: unknown): MarketplaceModule[] => {
  if (!res || typeof res !== 'object') return [];

  const content = (res as { data?: { content?: MarketplaceModule[] } }).data?.content;
  if (Array.isArray(content)) return content;

  if (Array.isArray((res as { content?: MarketplaceModule[] }).content)) {
    return (res as { content: MarketplaceModule[] }).content;
  }

  return normalizeMarketplaceModules(res);
};

/** Resolve request key used to match catalog modules (module id). */
export const resolveRequestedModuleKey = (item: MarketplaceModule): number =>
  Number(item.module ?? item.id ?? 0);

export const useGetMarketplaceService = () => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['get-marketplace-modules'],
    queryFn: async () => MarketplaceProfileService.getMarketplaceModules(),
    enabled: true,
    retry: 1,
    retryDelay: 36000000,
  });

  return {
    data,
    modules: normalizeMarketplaceModules(data),
    isLoading,
    error,
    refetch,
    isFetching,
    isDataFetching: isFetching,
  };
};

export const useGetMarketplacePagedService = (params?: {
  page?: number;
  size?: number;
  module?: number;
}) => {
  const page = params?.page ?? 1;
  const size = params?.size ?? 100;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['get-marketplace-paged', page, size, params?.module],
    queryFn: async () =>
      MarketplaceProfileService.getMarketplacePaged({
        page,
        size,
        ...(params?.module != null ? { module: params.module } : {}),
      }),
    // Disabled: requested state now comes from /api/marketplace/modules `status`
    // enabled: true,
    enabled: false,
    retry: 1,
    retryDelay: 36000000,
  });

  const requestedItems = normalizeMarketplacePagedContent(data);
  const requestedModuleIds = new Set(
    requestedItems.map(resolveRequestedModuleKey).filter((id) => id > 0),
  );

  return {
    data,
    requestedItems,
    requestedModuleIds,
    isLoading,
    error,
    refetch,
    isFetching,
    isDataFetching: isFetching,
  };
};

export const useRequestMarketplaceService = ({ onSuccess, onError }: RequestMarketplaceProps) => {
  const {
    mutateAsync: requestModule,
    isPending: isRequestLoading,
    data,
    error,
    isError,
  } = useMutation({
    mutationKey: ['request-marketplace-module'],
    mutationFn: (req: MarketplaceRequestPayload) => MarketplaceProfileService.requestMarketplaceModule(req),
    onSuccess: onSuccess,
    onError: onError,
  });

  const onRequestModule = useCallback(
    async (req: MarketplaceRequestPayload) => {
      try {
        await requestModule(req);
      } catch (err) {
        console.error(err);
      }
    },
    [requestModule],
  );

  return {
    onRequestModule,
    isRequestLoading,
    data,
    error,
    isError,
  };
};
